import { useState, useRef, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { ribbon, create, close, ellipsisVertical, checkmarkCircle } from 'ionicons/icons';
import type { ProfessionalAssociationMembership } from '@/types';

interface MembershipCardProps {
  membership: ProfessionalAssociationMembership;
  onEdit?: (m: ProfessionalAssociationMembership) => void;
  onDelete?: (m: ProfessionalAssociationMembership) => void;
}

const STATUS_COLORS: Record<string, string> = {
  Active: 'var(--bp-success, #22c55e)',
  Pending: 'var(--bp-warning, #f59e0b)',
  Expired: 'var(--bp-error, #ef4444)',
  Suspended: 'var(--bp-error, #ef4444)',
  Cancelled: 'var(--bp-text-muted, #64748b)',
  Unknown: 'var(--bp-text-muted, #64748b)',
};

export function MembershipCard({ membership, onEdit, onDelete }: MembershipCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const statusColor = STATUS_COLORS[membership.status] ?? STATUS_COLORS.Unknown;

  return (
    <div style={{
      padding: '12px 16px', borderRadius: 12,
      background: 'var(--bp-surface-raised, #1a2332)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IonIcon icon={ribbon} style={{ color: 'var(--bp-blue-400, #60a5fa)', fontSize: '1.1rem' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {membership.association.name}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted, #64748b)' }}>
              {membership.membershipNumber}
              {membership.membershipLevel ? ` · ${membership.membershipLevel}` : ''}
            </div>
          </div>
        </div>

        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Membership actions"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--bp-text-muted, #64748b)', padding: 4,
            }}
          >
            <IonIcon icon={ellipsisVertical} />
          </button>
          {menuOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 10,
              background: 'var(--bp-surface, #1e293b)',
              borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              minWidth: 120, overflow: 'hidden',
            }}>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onEdit?.(membership); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 12px', background: 'none', border: 'none',
                  color: 'var(--bp-text, #f1f5f9)', fontSize: '0.8rem', cursor: 'pointer',
                }}
              >
                <IonIcon icon={create} style={{ fontSize: '0.9rem' }} /> Edit
              </button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete?.(membership); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 12px', background: 'none', border: 'none',
                  color: 'var(--bp-error, #ef4444)', fontSize: '0.8rem', cursor: 'pointer',
                }}
              >
                <IonIcon icon={close} style={{ fontSize: '0.9rem' }} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
        flexWrap: 'wrap',
      }}>
        <span style={{
          fontSize: '0.7rem', padding: '2px 8px', borderRadius: 8,
          background: `${statusColor}20`, color: statusColor,
        }}>
          {membership.status}
        </span>
        {membership.verified && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: '0.7rem', color: 'var(--bp-success, #22c55e)',
          }}>
            <IonIcon icon={checkmarkCircle} style={{ fontSize: '0.8rem' }} />
            Verified
          </span>
        )}
        {membership.issuedDate && (
          <span style={{ fontSize: '0.7rem', color: 'var(--bp-text-muted, #64748b)' }}>
            Issued: {membership.issuedDate}
          </span>
        )}
        {membership.expiryDate && (
          <span style={{ fontSize: '0.7rem', color: 'var(--bp-text-muted, #64748b)' }}>
            Expires: {membership.expiryDate}
          </span>
        )}
      </div>
    </div>
  );
}
